/**
 * Collections API - Media Management
 *
 * Add, remove, and list media within collections.
 */

import { z } from 'zod'
import { PaginationMetaSchema } from '../common'
import { MediaTypeSchema } from './common'
import { MediaVisibilitySchema, MediaUploaderSchema, ProcessingStatusSchema } from '../media/common'

// ============================================================================
// GET /v1/collections/:id/media
// ============================================================================

export const GetCollectionMediaRequestSchema = z.object({
  limit: z.coerce.number().positive().max(100).optional().default(20),
  offset: z.coerce.number().min(0).optional().default(0),
  approved: z.coerce.boolean().optional(),
  mediaType: MediaTypeSchema.optional(),
  // SCH-2425: with no `mediaType`, the read returns PHOTO and VIDEO only; this
  // opts AUDIO in as well (post recordings land in the group's collection).
  // Opt-in so the web grids and the public embed, which cannot render a
  // recording, keep the visual-only default. DOCUMENT stays excluded either
  // way, and an explicit `mediaType` wins over it. A literal string enum, NOT
  // `z.coerce.boolean`, which reads the string 'false' as true.
  includeAudio: z
    .enum(['true', 'false'])
    .transform(v => v === 'true')
    .optional(),
  // SCH-1204: gallery default is `addedAt desc` — `sortOrder` is the photo's
  // index within its post, not a collection-wide key. See the backend schema
  // (`api/collections/v1/use-cases/get-media/schema.ts`) for the full rationale.
  // `orderDirection` has no default so each `orderBy` keeps its natural
  // direction server-side.
  orderBy: z.enum(['sortOrder', 'addedAt', 'capturedAt']).optional().default('addedAt'),
  orderDirection: z.enum(['asc', 'desc']).optional()
})

export type GetCollectionMediaRequest = z.infer<typeof GetCollectionMediaRequestSchema>

/** Collection context for media items (when fetched via collection) */
export const CollectionContextSchema = z.object({
  collectionId: z.string(),
  collectionName: z.string().optional(),
  collectionType: z.string().optional(),
  addedAt: z.coerce.date().optional(),
  // Caption for the media in this collection context
  caption: z.string().nullable().optional(),
  addedBy: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      photoUrl: z.string().nullable().optional()
    })
    .optional()
})

export const CollectionMediaItemSchema = z.object({
  id: z.string(),
  mediaId: z.string(),
  sortOrder: z.number(),
  isApproved: z.boolean(),
  addedAt: z.coerce.date(),
  // Optional collection context
  collectionContext: CollectionContextSchema.optional(),
  media: z.object({
    id: z.string(),
    mediaType: MediaTypeSchema,
    url: z.string(),
    thumbnailUrl: z.string().nullable().optional(),
    width: z.number().nullable().optional(),
    height: z.number().nullable().optional(),
    caption: z.string().nullable().optional(),
    capturedAt: z.coerce.date().nullable().optional(),
    // Additional fields for display
    cloudinaryUrl: z.string().nullable().optional(),
    cloudinaryThumbnailUrl: z.string().nullable().optional(),
    s3Key: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    originalFilename: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    // Extended fields
    visibility: MediaVisibilitySchema.optional(),
    fileSize: z.union([z.string(), z.number()]).nullable().optional(),
    uploadedBy: MediaUploaderSchema.nullable().optional(),
    // Media timestamps
    createdAt: z.coerce.date().optional(),
    // Video duration in seconds
    duration: z.number().nullable().optional(),
    // Mux fields (videos uploaded via direct Mux upload). When muxPlaybackId
    // is set, the web client should render via @mux/mux-player-react instead
    // of a native <video> tag. processingStatus surfaces encoding state so
    // PROCESSING items can show a placeholder rather than a broken thumb.
    muxPlaybackId: z.string().nullable().optional(),
    muxAssetId: z.string().nullable().optional(),
    processingStatus: ProcessingStatusSchema.nullable().optional(),
    // AUDIO rows only (SCH-2425): where the recording plays and downloads from.
    // A recording has no Cloudinary copy by design, so this is the only locator
    // a client gets. Absent on every photo, video and document row.
    audioUrl: z.string().nullable().optional()
  })
})

export type CollectionMediaItem = z.infer<typeof CollectionMediaItemSchema>

export const GetCollectionMediaResponseSchema = z.object({
  data: z.array(CollectionMediaItemSchema),
  message: z.string(),
  meta: PaginationMetaSchema
})

export type GetCollectionMediaResponse = z.infer<typeof GetCollectionMediaResponseSchema>

// Backwards compatibility aliases
export type GetCollectionMediaFilters = GetCollectionMediaRequest
export type ListCollectionMediaResponse = GetCollectionMediaResponse

// ============================================================================
// POST /v1/collections/:id/media
// ============================================================================

export const AddMediaToCollectionRequestSchema = z.object({
  mediaIds: z.array(z.string()).min(1).max(100)
})

export type AddMediaToCollectionRequest = z.infer<typeof AddMediaToCollectionRequestSchema>

export const AddMediaToCollectionResponseSchema = z.object({
  data: z.object({
    added: z.number(),
    skipped: z.number()
  }),
  message: z.string()
})

export type AddMediaToCollectionResponse = z.infer<typeof AddMediaToCollectionResponseSchema>

// ============================================================================
// DELETE /v1/collections/:id/media
// ============================================================================

export const RemoveMediaFromCollectionRequestSchema = z.object({
  mediaIds: z.array(z.string()).min(1).max(100)
})

export type RemoveMediaFromCollectionRequest = z.infer<typeof RemoveMediaFromCollectionRequestSchema>

export const RemoveMediaFromCollectionResponseSchema = z.object({
  data: z.object({
    removed: z.number()
  }),
  message: z.string()
})

export type RemoveMediaFromCollectionResponse = z.infer<typeof RemoveMediaFromCollectionResponseSchema>

// ============================================================================
// POST /v1/collections/:id/media/move
// ============================================================================

/**
 * Request to move media from a source collection (the `:id` path param) to a
 * destination collection. The move is atomic: the items are added to the
 * destination and removed from the source in a single backend transaction.
 */
export const MoveMediaBetweenCollectionsRequestSchema = z.object({
  mediaIds: z.array(z.string()).min(1).max(100),
  destinationCollectionId: z.string()
})

export type MoveMediaBetweenCollectionsRequest = z.infer<typeof MoveMediaBetweenCollectionsRequestSchema>

export const MoveMediaBetweenCollectionsResponseSchema = z.object({
  data: z.object({
    sourceCollectionId: z.string(),
    destinationCollectionId: z.string(),
    movedCount: z.number(),
    mediaIds: z.array(z.string())
  }),
  message: z.string()
})

export type MoveMediaBetweenCollectionsResponse = z.infer<typeof MoveMediaBetweenCollectionsResponseSchema>
